import * as pc from 'playcanvas';
import type { CollisionSystem } from '@viewer/collision/CollisionSystem';
import { GsplatMeasurePicker } from '@viewer/overlay/GsplatMeasurePicker';
import { PlayCanvasMeasureWorld } from '@viewer/playcanvas/PlayCanvasMeasureWorld';
import type { GizmoHighlight } from '@viewer/playcanvas/PlayCanvasMeasureGizmo';
import {
  cloneVec3,
  distance3,
  fmtMeters,
  nextMeasureLabel,
  reindexMeasureLabels,
  type Measurement,
  type MeasureSnapshot,
  type Vec3,
  type WorldAxis,
} from '@viewer/overlay/measureTypes';

const DRAG_EPSILON = 8;
const PICK_THROTTLE_MS = 50;

type ScreenPt = { x: number; y: number };

interface AxisDrag {
  measurementId: string;
  endpoint: 'a' | 'b';
  axis: WorldAxis;
  startPoint: Vec3;
  startMouse: ScreenPt;
  axisScreenDir: ScreenPt;
  pixelsPerUnit: number;
}

interface MeasurementDom {
  root: HTMLDivElement;
  letter: HTMLDivElement;
}

let measureIdSeq = 0;

export class MeasureOverlay {
  private toolActive = false;
  /** When true, canvas clicks place measurement points (Château-style picking-start). */
  private creating = false;
  private worldVisible = true;
  private readonly measurements: Measurement[] = [];
  private draftA: Vec3 | null = null;
  private draftPreview: Vec3 | null = null;
  private editingId: string | null = null;
  private gizmoHover: GizmoHighlight | null = null;
  private axisDrag: AxisDrag | null = null;
  private keyListenerOn = false;
  private chromeVisible = false;
  private islandExpanded = false;
  private suppressPointerLockClick = false;
  private pendingClick: ScreenPt | null = null;
  private dragExceeded = false;
  private lastPreviewPickMs = 0;

  private readonly root: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly cameraEntity: pc.Entity;
  private readonly device: pc.GraphicsDevice;
  private readonly collision: CollisionSystem | null;
  private readonly requestRender: () => void;
  private readonly onRequestExit?: () => void;
  private readonly onActiveChange?: (active: boolean) => void;
  private readonly gsplatPicker: GsplatMeasurePicker;
  private readonly worldGraphics: PlayCanvasMeasureWorld;

  private readonly worldLayer: HTMLDivElement;
  private readonly draftLayer: HTMLDivElement;
  private readonly draftDotA: HTMLDivElement;
  private readonly draftDotP: HTMLDivElement;
  private readonly draftLine: HTMLDivElement;

  private readonly uiRoot: HTMLDivElement;
  private readonly listPanel: HTMLDivElement;
  private readonly panelList: HTMLUListElement;
  private readonly panelAddBtn: HTMLButtonElement;
  private readonly panelHint: HTMLSpanElement;
  private readonly island: HTMLDivElement;
  private readonly islandRulerBtn: HTMLButtonElement;
  private readonly islandPencilBtn: HTMLButtonElement;
  private readonly islandEyeBtn: HTMLButtonElement;

  private readonly measurementDom = new Map<string, MeasurementDom>();

  private readonly near = new pc.Vec3();
  private readonly far = new pc.Vec3();
  private readonly dir = new pc.Vec3();
  private readonly rayOrigin = new pc.Vec3();
  private readonly worldPt = new pc.Vec3();
  private readonly toPoint = new pc.Vec3();
  private readonly camPos = new pc.Vec3();
  private readonly camFwd = new pc.Vec3();
  private readonly axisTip = new pc.Vec3();

  private boundKey: (e: KeyboardEvent) => void;

  constructor(opts: {
    app: pc.AppBase;
    root: HTMLElement;
    panelRoot: HTMLElement;
    canvas: HTMLCanvasElement;
    cameraEntity: pc.Entity;
    device: pc.GraphicsDevice;
    collision: CollisionSystem | null;
    requestRender: () => void;
    onRequestExit?: () => void;
    onActiveChange?: (active: boolean) => void;
  }) {
    this.root = opts.root;
    this.canvas = opts.canvas;
    this.cameraEntity = opts.cameraEntity;
    this.device = opts.device;
    this.collision = opts.collision;
    this.requestRender = opts.requestRender;
    this.onRequestExit = opts.onRequestExit;
    this.onActiveChange = opts.onActiveChange;
    this.gsplatPicker = new GsplatMeasurePicker(
      opts.app,
      opts.canvas,
      opts.cameraEntity.camera!,
    );
    this.worldGraphics = new PlayCanvasMeasureWorld(opts.app);

    this.worldLayer = document.createElement('div');
    this.worldLayer.className = 'gii-measure-world';
    this.root.appendChild(this.worldLayer);

    this.draftLayer = document.createElement('div');
    this.draftLayer.className = 'gii-measure-draft';
    this.worldLayer.appendChild(this.draftLayer);
    this.draftDotA = this.makeDot('gii-measure-dot gii-measure-a');
    this.draftDotP = this.makeDot('gii-measure-dot gii-measure-preview');
    this.draftLine = document.createElement('div');
    this.draftLine.className = 'gii-measure-line';
    this.draftLayer.appendChild(this.draftLine);
    this.draftLayer.appendChild(this.draftDotA);
    this.draftLayer.appendChild(this.draftDotP);

    this.uiRoot = document.createElement('div');
    this.uiRoot.className = 'gii-measure-ui gii-viewer-chrome gii-viewer-chrome--hidden';

    this.listPanel = document.createElement('div');
    this.listPanel.className = 'gii-measure-list';
    this.listPanel.hidden = true;
    this.listPanel.innerHTML = `
      <div class="gii-measure-list-actions">
        <button type="button" class="gii-measure-list-add">+ Add</button>
        <span class="gii-measure-hint"></span>
      </div>
      <ul class="gii-measure-list-items"></ul>
    `;
    this.panelList = this.listPanel.querySelector('.gii-measure-list-items') as HTMLUListElement;
    this.panelAddBtn = this.listPanel.querySelector('.gii-measure-list-add') as HTMLButtonElement;
    this.panelHint = this.listPanel.querySelector('.gii-measure-hint') as HTMLSpanElement;
    this.panelAddBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.startCreating();
    });

    this.island = document.createElement('div');
    this.island.className = 'gii-measure-island';
    this.island.innerHTML = `
      <button type="button" class="gii-measure-island-btn gii-measure-island-btn--ruler" title="Measurement tools" aria-label="Measurement tools" aria-expanded="false"></button>
      <div class="gii-measure-island-extra">
        <button type="button" class="gii-measure-island-btn gii-measure-island-btn--pencil" title="Edit measurements" aria-label="Edit measurements"></button>
        <button type="button" class="gii-measure-island-btn gii-measure-island-btn--eye" title="Show measurements" aria-label="Show measurements" aria-pressed="true"></button>
      </div>
    `;
    this.islandRulerBtn = this.island.querySelector('.gii-measure-island-btn--ruler') as HTMLButtonElement;
    this.islandPencilBtn = this.island.querySelector('.gii-measure-island-btn--pencil') as HTMLButtonElement;
    this.islandEyeBtn = this.island.querySelector('.gii-measure-island-btn--eye') as HTMLButtonElement;
    this.islandRulerBtn.innerHTML = MeasureOverlay.iconRuler();
    this.islandPencilBtn.innerHTML = MeasureOverlay.iconPencil();
    this.islandEyeBtn.innerHTML = MeasureOverlay.iconEye(true);

    this.islandRulerBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.islandExpanded) {
        if (this.toolActive) this.setEditMode(false);
        this.islandExpanded = false;
      } else {
        this.islandExpanded = true;
      }
      this.syncUi();
    });
    this.islandPencilBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleEditMode();
    });
    this.islandEyeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.worldVisible = !this.worldVisible;
      this.applyWorldVisibility();
      this.syncUi();
      this.requestRender();
    });

    this.uiRoot.append(this.island, this.listPanel);
    opts.panelRoot.appendChild(this.uiRoot);

    this.injectCssOnce();

    this.boundKey = (e) => this.onKeyDown(e);
    this.syncUi();
  }

  /** Measure tool owns canvas pointer while placing or dragging a gizmo axis. */
  capturesPointer(): boolean {
    if (this.axisDrag !== null) return true;
    if (!this.toolActive) return false;
    return this.creating || this.draftA !== null;
  }

  isEditing(): boolean {
    return this.editingId !== null;
  }

  /** Edit-mode list open — pointer lock stays off while this is true. */
  isEditMode(): boolean {
    return this.toolActive;
  }

  blocksPointerLock(): boolean {
    return (
      this.editingId !== null ||
      this.axisDrag !== null ||
      this.creating ||
      this.draftA !== null
    );
  }

  consumeSuppressPointerLock(): boolean {
    if (!this.suppressPointerLockClick) return false;
    this.suppressPointerLockClick = false;
    return true;
  }

  toggleEditMode(): void {
    this.setEditMode(!this.toolActive);
  }

  setToolActive(active: boolean): void {
    this.setEditMode(active);
  }

  setEditMode(active: boolean): void {
    if (this.toolActive === active) return;
    this.toolActive = active;
    if (active) {
      this.releasePointerLock();
      this.creating = false;
    } else {
      this.clearDraft();
      this.exitEdit();
      this.creating = false;
    }
    this.syncKeyListener();
    this.onActiveChange?.(active);
    this.syncUi();
    this.requestRender();
  }

  /** Screen-space gizmo pick when editing — returns true if an axis drag started. */
  tryStartGizmoDrag(clientX: number, clientY: number): boolean {
    if (!this.editingId) return false;
    const pick = this.pickGizmoAxis(clientX, clientY);
    if (!pick) return false;
    this.beginAxisDrag(pick, clientX, clientY);
    return true;
  }

  /** Sync 3D + overlay visibility immediately before a canvas capture. */
  refreshForCapture(): void {
    this.applyWorldVisibility();
    this.syncEditGizmo();
  }

  /** True when measure labels should be composited onto a canvas capture. */
  shouldCompositeForCapture(): boolean {
    return this.worldVisible && this.measurements.length > 0;
  }

  /** Draw letter labels onto a cropped canvas capture (Show on). */
  compositeCaptureLabels(
    ctx: CanvasRenderingContext2D,
    crop: { sx: number; sy: number; cw: number; ch: number },
  ): void {
    if (!this.shouldCompositeForCapture()) return;
    const cam = this.cameraEntity.camera;
    if (!cam) return;

    ctx.save();
    ctx.font = '700 13px system-ui, -apple-system, Segoe UI, Roboto, Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    for (const m of this.measurements) {
      const mid = {
        x: (m.a.x + m.b.x) * 0.5,
        y: (m.a.y + m.b.y) * 0.5,
        z: (m.a.z + m.b.z) * 0.5,
      };
      if (!this.isInFrontOfCamera(mid)) continue;
      this.worldPt.set(mid.x, mid.y, mid.z);
      const s = cam.worldToScreen(this.worldPt);
      const x = s.x - crop.sx;
      const y = s.y - crop.sy;
      if (x < -40 || y < -40 || x > crop.cw + 40 || y > crop.ch + 40) continue;

      const text = this.toolActive ? m.label : fmtMeters(distance3(m.a, m.b));
      const metrics = ctx.measureText(text);
      const padX = 7;
      const padY = 4;
      const w = metrics.width + padX * 2;
      const h = 13 + padY * 2;
      const bx = x - w / 2;
      const by = y - h - 6;

      ctx.fillStyle = 'rgba(0,0,0,0.72)';
      ctx.fillRect(bx, by, w, h);
      ctx.strokeStyle = 'rgba(255,255,255,0.25)';
      ctx.lineWidth = 1;
      ctx.strokeRect(bx + 0.5, by + 0.5, w - 1, h - 1);
      ctx.fillStyle = '#fff';
      ctx.fillText(text, x, by + h / 2);
    }
    ctx.restore();
  }

  /** True while axis gizmo is being dragged — used to keep camera unlocked for look. */
  isDraggingGizmo(): boolean {
    return this.axisDrag !== null;
  }

  /** Legacy hook — camera stays free during measure (Château-style). */
  shouldLockCamera(): boolean {
    return false;
  }

  isActive(): boolean {
    return this.toolActive;
  }

  setChromeVisible(visible: boolean): void {
    if (this.chromeVisible === visible) return;
    this.chromeVisible = visible;
    this.uiRoot.classList.toggle('gii-viewer-chrome--hidden', !visible);
    this.uiRoot.classList.toggle('gii-viewer-chrome--ready', visible);
  }

  /** True while placing a measurement or dragging a gizmo axis. */
  isPlacing(): boolean {
    if (!this.toolActive) return false;
    return this.creating || this.draftA !== null || this.axisDrag !== null;
  }

  needsTick(): boolean {
    return (
      this.toolActive ||
      this.editingId !== null ||
      this.measurements.length > 0 ||
      this.draftA !== null
    );
  }

  getSnapshot(): MeasureSnapshot {
    return {
      measurements: this.measurements.map((m) => ({
        id: m.id,
        label: m.label,
        distanceM: distance3(m.a, m.b),
        editing: this.editingId === m.id,
      })),
      worldVisible: this.worldVisible,
      toolActive: this.toolActive,
      placing: this.isPlacing(),
    };
  }

  private syncKeyListener(): void {
    const want = this.toolActive || this.editingId !== null;
    if (want && !this.keyListenerOn) {
      window.addEventListener('keydown', this.boundKey, { capture: true });
      this.keyListenerOn = true;
    } else if (!want && this.keyListenerOn) {
      window.removeEventListener('keydown', this.boundKey, { capture: true } as never);
      this.keyListenerOn = false;
    }
  }

  dispose(): void {
    this.setEditMode(false);
    this.syncKeyListener();
    this.gsplatPicker.dispose();
    this.worldGraphics.dispose();
    for (const id of [...this.measurementDom.keys()]) this.removeMeasurementDom(id);
    this.worldLayer.remove();
    this.uiRoot.remove();
  }

  tick(): void {
    this.applyWorldVisibility();
    this.renderDraftLabels();
    for (const m of this.measurements) {
      this.renderMeasurementLabels(m);
    }
    this.syncEditGizmo();
  }

  private applyWorldVisibility(): void {
    this.syncWorldVisibility();
    this.syncWorldGraphics();
  }

  private syncEditGizmo(): void {
    if (!this.editingId || !this.worldVisible) {
      this.worldGraphics.syncEditGizmo({
        visible: false,
        pointA: null,
        pointB: null,
        lengthA: 0,
        lengthB: 0,
        hover: null,
        active: null,
      });
      return;
    }
    const m = this.measurements.find((x) => x.id === this.editingId);
    if (!m) return;
    const active: GizmoHighlight | null = this.axisDrag
      ? { endpoint: this.axisDrag.endpoint, axis: this.axisDrag.axis }
      : null;
    this.worldGraphics.syncEditGizmo({
      visible: true,
      pointA: m.a,
      pointB: m.b,
      lengthA: this.worldGraphics.gizmoAxisLength(m.a, this.cameraEntity),
      lengthB: this.worldGraphics.gizmoAxisLength(m.b, this.cameraEntity),
      hover: this.axisDrag ? null : this.gizmoHover,
      active,
    });
  }

  private syncWorldGraphics(): void {
    const show = this.worldVisible && (this.measurements.length > 0 || this.draftA !== null);
    this.worldGraphics.setVisible(show);
    if (!show) return;

    const liveIds = new Set(this.measurements.map((m) => m.id));
    for (const id of [...this.segmentsDomIds()]) {
      if (!liveIds.has(id)) this.worldGraphics.removeSegment(id);
    }
    for (const m of this.measurements) {
      this.worldGraphics.syncMeasurement(m.id, m.a, m.b);
    }

    if (this.draftA) {
      const preview = this.draftPreview ?? this.draftA;
      this.worldGraphics.syncDraft(this.draftA, preview);
    } else {
      this.worldGraphics.syncDraft(null, null);
    }
  }

  private *segmentsDomIds(): Generator<string> {
    for (const m of this.measurements) yield m.id;
  }

  private startCreating(): void {
    if (!this.toolActive) this.setEditMode(true);
    this.exitEdit();
    this.clearDraft();
    this.creating = true;
    this.releasePointerLock();
    this.syncUi();
    this.requestRender();
  }

  private releasePointerLock(): void {
    if (document.pointerLockElement === this.canvas) {
      document.exitPointerLock();
    }
  }

  private beginAxisDrag(pick: GizmoHighlight, clientX: number, clientY: number): void {
    if (!this.editingId) return;
    const m = this.measurements.find((x) => x.id === this.editingId);
    if (!m) return;
    const point = pick.endpoint === 'a' ? m.a : m.b;
    const scale = this.axisScreenScale(point, pick.axis);
    if (!scale) return;
    this.releasePointerLock();
    this.axisDrag = {
      measurementId: m.id,
      endpoint: pick.endpoint,
      axis: pick.axis,
      startPoint: cloneVec3(point),
      startMouse: { x: clientX, y: clientY },
      axisScreenDir: scale.dir,
      pixelsPerUnit: scale.pixelsPerUnit,
    };
    this.gizmoHover = null;
    this.pendingClick = null;
    this.dragExceeded = false;
    this.requestRender();
  }

  /** Called from viewer input router (capture phase) — pointerdown on canvas. */
  handlePointerDown(clientX: number, clientY: number): void {
    if (!this.creating) return;
    this.releasePointerLock();
    this.pendingClick = { x: clientX, y: clientY };
    this.dragExceeded = false;
  }

  /** Called from viewer input router — pointermove on canvas. */
  handlePointerMove(clientX: number, clientY: number): void {
    if (this.axisDrag) {
      this.applyAxisDrag(this.axisDrag, clientX, clientY);
      return;
    }

    if (this.editingId) {
      const hover = this.pickGizmoAxis(clientX, clientY);
      const changed =
        hover?.axis !== this.gizmoHover?.axis ||
        hover?.endpoint !== this.gizmoHover?.endpoint;
      if (changed) {
        this.gizmoHover = hover;
        this.requestRender();
      }
    }

    if (this.pendingClick) {
      const dx = clientX - this.pendingClick.x;
      const dy = clientY - this.pendingClick.y;
      if (dx * dx + dy * dy > DRAG_EPSILON * DRAG_EPSILON) {
        this.dragExceeded = true;
      }
    }

    if (!this.draftA || !this.creating || this.gsplatPicker.isBusy()) return;
    const now = performance.now();
    if (now - this.lastPreviewPickMs < PICK_THROTTLE_MS) return;
    this.lastPreviewPickMs = now;
    void this.pickWorldPointAsync(clientX, clientY).then((hit) => {
      if (!hit || !this.draftA) return;
      this.draftPreview = hit;
      this.requestRender();
    });
  }

  /** Called from viewer input router — pointerup on canvas. */
  handlePointerUp(): void {
    if (this.axisDrag) {
      this.axisDrag = null;
      this.suppressPointerLockClick = true;
      this.requestRender();
      return;
    }

    if (!this.pendingClick || this.dragExceeded || !this.creating) {
      this.pendingClick = null;
      return;
    }

    const click = this.pendingClick;
    this.pendingClick = null;
    void this.pickWorldPointAsync(click.x, click.y).then((hit) => {
      if (!hit) return;
      if (!this.draftA) {
        this.draftA = hit;
        this.draftPreview = hit;
        this.syncUi();
        this.syncWorldVisibility();
      } else {
        this.commitDraft(hit);
      }
      this.requestRender();
    });
  }

  private makeDot(className: string): HTMLDivElement {
    const el = document.createElement('div');
    el.className = className;
    return el;
  }

  private ensureMeasurementDom(m: Measurement): MeasurementDom {
    let dom = this.measurementDom.get(m.id);
    if (dom) return dom;

    const root = document.createElement('div');
    root.className = 'gii-measure-item';
    root.dataset.id = m.id;

    const letter = document.createElement('div');
    letter.className = 'gii-measure-letter';

    root.append(letter);
    this.worldLayer.appendChild(root);

    dom = { root, letter };
    this.measurementDom.set(m.id, dom);
    return dom;
  }

  private removeMeasurementDom(id: string): void {
    const dom = this.measurementDom.get(id);
    if (!dom) return;
    dom.root.remove();
    this.measurementDom.delete(id);
  }

  private syncWorldVisibility(): void {
    const showOverlay =
      this.worldVisible && (this.measurements.length > 0 || this.draftA !== null);
    this.worldLayer.style.display = showOverlay ? '' : 'none';
    this.worldLayer.style.pointerEvents = 'none';
    this.draftLayer.style.display = 'none';
  }

  private syncUi(): void {
    this.syncList();
    this.syncIsland();
  }

  private syncIsland(): void {
    this.island.classList.toggle('gii-measure-island--expanded', this.islandExpanded);
    this.islandRulerBtn.classList.toggle('gii-measure-island-btn--active', this.islandExpanded);
    this.islandRulerBtn.setAttribute('aria-expanded', String(this.islandExpanded));
    this.islandPencilBtn.classList.toggle('gii-measure-island-btn--active', this.toolActive);
    this.islandEyeBtn.classList.toggle('gii-measure-island-btn--active', this.worldVisible);
    this.islandEyeBtn.classList.toggle('gii-measure-island-btn--muted', !this.worldVisible);
    this.islandEyeBtn.setAttribute('aria-pressed', String(this.worldVisible));
    this.islandPencilBtn.setAttribute('aria-pressed', String(this.toolActive));
    this.islandRulerBtn.classList.toggle(
      'gii-measure-island-btn--active',
      this.creating || this.draftA !== null,
    );
    this.islandEyeBtn.innerHTML = MeasureOverlay.iconEye(this.worldVisible);
  }

  private syncList(): void {
    this.listPanel.hidden = !this.toolActive;
    this.panelAddBtn.hidden = !this.toolActive || this.creating || this.draftA !== null;
    if (this.draftA) {
      this.panelHint.textContent = 'Click second point · Esc cancels';
    } else if (this.creating) {
      this.panelHint.textContent = 'Click first point · Esc stops';
    } else if (this.editingId) {
      this.panelHint.textContent = 'Drag axes · pick ✓ in list to finish';
    } else if (this.toolActive) {
      this.panelHint.textContent =
        this.measurements.length === 0
          ? 'Tap ruler to add · Esc to exit'
          : 'Ruler to add · click row ✎ to adjust';
    } else {
      this.panelHint.textContent = '';
    }
    this.panelList.replaceChildren();

    for (const m of this.measurements) {
      const li = document.createElement('li');
      li.className = 'gii-measure-list-row';
      if (this.editingId === m.id) li.classList.add('gii-measure-list-row--editing');

      const label = document.createElement('span');
      label.className = 'gii-measure-list-label';
      label.textContent = `${m.label}: ${fmtMeters(distance3(m.a, m.b))}`;

      const editBtn = document.createElement('button');
      editBtn.type = 'button';
      editBtn.className = 'gii-measure-list-btn';
      editBtn.title = this.editingId === m.id ? 'Done editing' : 'Edit endpoints';
      editBtn.setAttribute('aria-label', editBtn.title);
      editBtn.innerHTML = this.editingId === m.id ? MeasureOverlay.iconCheck() : MeasureOverlay.iconPencilSmall();
      editBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleEdit(m.id);
      });

      const delBtn = document.createElement('button');
      delBtn.type = 'button';
      delBtn.className = 'gii-measure-list-btn gii-measure-list-btn--danger';
      delBtn.title = 'Delete measurement';
      delBtn.setAttribute('aria-label', delBtn.title);
      delBtn.innerHTML = MeasureOverlay.iconTrash();
      delBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.deleteMeasurement(m.id);
      });

      li.append(label, editBtn, delBtn);
      this.panelList.appendChild(li);
    }
  }

  private toggleEdit(id: string): void {
    if (this.editingId === id) {
      this.exitEdit();
    } else {
      this.creating = false;
      this.clearDraft();
      this.editingId = id;
      this.axisDrag = null;
      this.gizmoHover = null;
      this.releasePointerLock();
    }
    this.syncKeyListener();
    this.syncUi();
    this.requestRender();
  }

  private exitEdit(): void {
    if (!this.editingId && !this.axisDrag) return;
    const shouldRelock = this.editingId !== null && this.toolActive;
    this.editingId = null;
    this.axisDrag = null;
    this.gizmoHover = null;
    this.syncKeyListener();
    this.syncUi();
    this.applyWorldVisibility();
    this.requestRender();
    if (shouldRelock && document.pointerLockElement !== this.canvas) {
      void this.canvas.requestPointerLock();
    }
  }

  private deleteMeasurement(id: string): void {
    const idx = this.measurements.findIndex((m) => m.id === id);
    if (idx < 0) return;
    this.measurements.splice(idx, 1);
    reindexMeasureLabels(this.measurements);
    this.removeMeasurementDom(id);
    this.worldGraphics.removeSegment(id);
    if (this.editingId === id) this.exitEdit();
    this.syncUi();
    this.applyWorldVisibility();
    this.requestRender();
  }

  private commitDraft(b: Vec3): void {
    if (!this.draftA) return;
    const m: Measurement = {
      id: `m-${++measureIdSeq}`,
      label: nextMeasureLabel(this.measurements.length),
      a: cloneVec3(this.draftA),
      b: cloneVec3(b),
    };
    this.measurements.push(m);
    this.draftA = null;
    this.draftPreview = null;
    this.creating = false;
    this.syncUi();
    this.applyWorldVisibility();
    this.requestRender();
  }

  private clearDraft(): void {
    if (!this.draftA && !this.draftPreview) return;
    this.draftA = null;
    this.draftPreview = null;
    this.pendingClick = null;
    this.dragExceeded = false;
    this.syncUi();
    this.applyWorldVisibility();
    this.requestRender();
  }

  private layoutOffsets(): { offX: number; offY: number } {
    const canvasRect = this.canvas.getBoundingClientRect();
    const rootRect = this.root.getBoundingClientRect();
    return {
      offX: canvasRect.left - rootRect.left,
      offY: canvasRect.top - rootRect.top,
    };
  }

  private project(v: Vec3): ScreenPt | null {
    const cam = this.cameraEntity.camera;
    if (!cam || !this.isInFrontOfCamera(v)) return null;
    const { offX, offY } = this.layoutOffsets();
    this.worldPt.set(v.x, v.y, v.z);
    const s = cam.worldToScreen(this.worldPt);
    return { x: offX + s.x, y: offY + s.y };
  }

  private isInFrontOfCamera(v: Vec3): boolean {
    this.camPos.copy(this.cameraEntity.getPosition());
    this.camFwd.copy(this.cameraEntity.forward);
    this.toPoint.set(v.x - this.camPos.x, v.y - this.camPos.y, v.z - this.camPos.z);
    return this.toPoint.dot(this.camFwd) > 0.05;
  }

  private renderDraftLabels(): void {
    // Draft endpoints/lines are 3D only until committed.
  }

  private renderMeasurementLabels(m: Measurement): void {
    const dom = this.ensureMeasurementDom(m);
    if (!this.worldVisible) {
      dom.letter.style.display = 'none';
      return;
    }
    dom.letter.textContent = this.toolActive
      ? m.label
      : fmtMeters(distance3(m.a, m.b));

    const mid = {
      x: (m.a.x + m.b.x) * 0.5,
      y: (m.a.y + m.b.y) * 0.5,
      z: (m.a.z + m.b.z) * 0.5,
    };
    const pm = this.project(mid);
    if (pm) {
      dom.letter.style.display = '';
      dom.letter.style.left = `${pm.x}px`;
      dom.letter.style.top = `${pm.y}px`;
    } else {
      dom.letter.style.display = 'none';
    }
  }

  private axisWorldUnit(axis: WorldAxis): Vec3 {
    if (axis === 'x') return { x: 1, y: 0, z: 0 };
    if (axis === 'y') return { x: 0, y: 1, z: 0 };
    return { x: 0, y: 0, z: 1 };
  }

  private axisScreenScale(origin: Vec3, axis: WorldAxis): { dir: ScreenPt; pixelsPerUnit: number } | null {
    const unit = this.axisWorldUnit(axis);
    this.axisTip.set(origin.x + unit.x, origin.y + unit.y, origin.z + unit.z);
    const p0 = this.project(origin);
    const p1 = this.project({ x: this.axisTip.x, y: this.axisTip.y, z: this.axisTip.z });
    if (!p0 || !p1) return null;
    const dx = p1.x - p0.x;
    const dy = p1.y - p0.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 1e-3) return null;
    return { dir: { x: dx / len, y: dy / len }, pixelsPerUnit: len };
  }

  private pickGizmoAxis(clientX: number, clientY: number): GizmoHighlight | null {
    if (!this.editingId) return null;
    const m = this.measurements.find((x) => x.id === this.editingId);
    if (!m) return null;

    const rootRect = this.root.getBoundingClientRect();
    const cx = clientX - rootRect.left;
    const cy = clientY - rootRect.top;

    let best: { pick: GizmoHighlight; perpSq: number } | null = null;

    for (const endpoint of ['a', 'b'] as const) {
      const point = endpoint === 'a' ? m.a : m.b;
      const origin = this.project(point);
      if (!origin) continue;
      const worldLen = this.worldGraphics.gizmoAxisLength(point, this.cameraEntity);

      for (const axis of ['x', 'y', 'z'] as const) {
        const scale = this.axisScreenScale(point, axis);
        if (!scale) continue;
        const screenLen = scale.pixelsPerUnit * worldLen;
        const dx = cx - origin.x;
        const dy = cy - origin.y;
        const along = dx * scale.dir.x + dy * scale.dir.y;
        const perpX = dx - along * scale.dir.x;
        const perpY = dy - along * scale.dir.y;
        const perpSq = perpX * perpX + perpY * perpY;
        if (perpSq <= 16 * 16 && along >= -8 && along <= screenLen + 10) {
          if (!best || perpSq < best.perpSq) {
            best = { pick: { endpoint, axis }, perpSq };
          }
        }
      }
    }
    return best?.pick ?? null;
  }

  private applyAxisDrag(drag: AxisDrag, clientX: number, clientY: number): void {
    const m = this.measurements.find((x) => x.id === drag.measurementId);
    if (!m) return;
    const pt = drag.endpoint === 'a' ? m.a : m.b;
    const dx = clientX - drag.startMouse.x;
    const dy = clientY - drag.startMouse.y;
    const along = dx * drag.axisScreenDir.x + dy * drag.axisScreenDir.y;
    const delta = drag.pixelsPerUnit > 1e-6 ? along / drag.pixelsPerUnit : 0;
    pt.x = drag.startPoint.x;
    pt.y = drag.startPoint.y;
    pt.z = drag.startPoint.z;
    if (drag.axis === 'x') pt.x = drag.startPoint.x + delta;
    if (drag.axis === 'y') pt.y = drag.startPoint.y + delta;
    if (drag.axis === 'z') pt.z = drag.startPoint.z + delta;
    this.requestRender();
  }

  private async pickWorldPointAsync(clientX: number, clientY: number): Promise<Vec3 | null> {
    const gsplatHit = await this.gsplatPicker.pick(clientX, clientY);
    if (gsplatHit && this.isInFrontOfCamera(gsplatHit)) return gsplatHit;
    return this.pickWorldPointFallback(clientX, clientY);
  }

  private onKeyDown(e: KeyboardEvent): void {
    if (!this.toolActive && !this.editingId) return;
    if (e.code === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (this.editingId) {
        this.exitEdit();
      } else if (this.draftA) {
        this.clearDraft();
      } else if (this.creating) {
        this.creating = false;
        this.syncUi();
        this.requestRender();
      } else if (this.toolActive) {
        this.setEditMode(false);
        this.onRequestExit?.();
      } else {
        this.onRequestExit?.();
      }
    }
    if (e.code === 'Backspace' && this.draftA) {
      e.preventDefault();
      e.stopPropagation();
      this.clearDraft();
    }
  }

  private clientToScreen(clientX: number, clientY: number): ScreenPt | null {
    const rect = this.canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    if (x < 0 || y < 0 || x > rect.width || y > rect.height) return null;
    const cw = this.canvas.clientWidth;
    const ch = this.canvas.clientHeight;
    if (cw <= 0 || ch <= 0) return null;
    return { x: (x / rect.width) * cw, y: (y / rect.height) * ch };
  }

  private pickWorldPointFallback(clientX: number, clientY: number): Vec3 | null {
    const screen = this.clientToScreen(clientX, clientY);
    if (!screen) return null;

    const cam = this.cameraEntity.camera;
    if (!cam) return null;

    cam.screenToWorld(screen.x, screen.y, cam.nearClip, this.near);
    cam.screenToWorld(screen.x, screen.y, cam.farClip, this.far);
    this.dir.sub2(this.far, this.near).normalize();
    this.rayOrigin.copy(this.near);

    if (this.collision?.isLoaded) {
      const dist = this.collision.raycastDistance(
        { x: this.rayOrigin.x, y: this.rayOrigin.y, z: this.rayOrigin.z },
        { x: this.dir.x, y: this.dir.y, z: this.dir.z },
        500,
      );
      if (dist != null) {
        const hit = {
          x: this.rayOrigin.x + this.dir.x * dist,
          y: this.rayOrigin.y + this.dir.y * dist,
          z: this.rayOrigin.z + this.dir.z * dist,
        };
        return this.isInFrontOfCamera(hit) ? hit : null;
      }
    }

    if (Math.abs(this.dir.y) < 1e-6) return null;
    const t = -this.rayOrigin.y / this.dir.y;
    if (t <= 0) return null;
    const hit = {
      x: this.rayOrigin.x + this.dir.x * t,
      y: 0,
      z: this.rayOrigin.z + this.dir.z * t,
    };
    return this.isInFrontOfCamera(hit) ? hit : null;
  }

  private injectCssOnce(): void {
    let el = document.getElementById('gii-measure-css') as HTMLStyleElement | null;
    if (!el) {
      el = document.createElement('style');
      el.id = 'gii-measure-css';
      document.head.appendChild(el);
    }
    el.textContent = `
.gii-measure-world, .gii-measure-draft {
  position: absolute; inset: 0; pointer-events: none; z-index: 40; touch-action: none;
}
.gii-measure-item { position: absolute; inset: 0; pointer-events: none; }
.gii-measure-letter {
  position: absolute; transform: translate(-50%, -180%);
  min-width: 1.4rem; text-align: center;
  padding: 3px 8px; border-radius: 8px;
  font: 600 12px/1.2 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  background: rgba(0,0,0,0.76); color: #fff;
  box-shadow: 0 1px 8px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.18);
  pointer-events: none; white-space: nowrap;
}
.gii-measure-ui {
  position: absolute; left: 0.65rem; bottom: 0.65rem; z-index: 55;
  pointer-events: none;
}
.gii-measure-island {
  display: flex; align-items: center;
  position: relative;
  pointer-events: auto;
  background: rgba(8, 16, 30, 0.82);
  border: 1px solid rgba(255,255,255,0.16);
  border-radius: 999px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06);
  backdrop-filter: blur(14px);
  padding: 0.28rem;
  transition: padding 0.22s ease, box-shadow 0.22s ease;
}
.gii-measure-island--expanded { padding: 0.28rem 0.35rem 0.28rem 0.28rem; }
.gii-measure-island-extra {
  display: flex; align-items: center; gap: 0.15rem;
  max-width: 0; opacity: 0; overflow: hidden;
  transition: max-width 0.28s ease, opacity 0.2s ease, margin 0.28s ease;
  margin-left: 0;
}
.gii-measure-island--expanded .gii-measure-island-extra {
  max-width: 6rem; opacity: 1; margin-left: 0.15rem;
}
.gii-measure-island-btn {
  display: grid; place-items: center;
  width: 2.35rem; height: 2.35rem;
  border: none; border-radius: 999px;
  background: transparent; color: rgba(255,255,255,0.82);
  cursor: pointer; padding: 0; flex-shrink: 0;
  transition: background 0.15s, color 0.15s, transform 0.12s;
}
.gii-measure-icon {
  display: block;
  width: 1.28rem;
  height: 1.28rem;
  background: currentColor;
}
.gii-measure-icon--ruler {
  -webkit-mask: url('/artwork/icon_measure01.svg') center / contain no-repeat;
  mask: url('/artwork/icon_measure01.svg') center / contain no-repeat;
}
.gii-measure-island-btn svg { display: block; }
.gii-measure-island-btn:hover {
  background: rgba(255,255,255,0.1); color: #fff;
}
.gii-measure-island-btn:active { transform: scale(0.94); }
.gii-measure-island-btn--active {
  background: rgba(100, 180, 255, 0.22);
  color: #b8e4ff;
  box-shadow: inset 0 0 0 1px rgba(120, 200, 255, 0.35);
}
.gii-measure-island-btn--muted { color: rgba(255,255,255,0.38); }
.gii-measure-island-btn--pencil { margin-left: 0.05rem; }
.gii-measure-island-extra::before {
  content: ''; width: 1px; height: 1.35rem;
  background: rgba(255,255,255,0.14); margin-right: 0.1rem; flex-shrink: 0;
}
.gii-measure-list {
  position: absolute; left: 0; bottom: calc(100% + 0.45rem);
  width: min(16.5rem, calc(100vw - 1.3rem));
  pointer-events: auto;
  background: rgba(8, 16, 30, 0.9);
  border: 1px solid rgba(255,255,255,0.16);
  border-radius: 0.75rem;
  color: #f5f9ff; backdrop-filter: blur(14px);
  box-shadow: 0 8px 32px rgba(0,0,0,0.35);
  font: 13px/1.35 system-ui, -apple-system, Segoe UI, Roboto, Arial;
  overflow: hidden;
}
.gii-measure-list-actions {
  display: flex; flex-wrap: wrap; align-items: center; gap: 0.35rem 0.5rem;
  padding: 0.5rem 0.55rem; border-bottom: 1px solid rgba(255,255,255,0.1);
}
.gii-measure-list-add {
  background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.22);
  border-radius: 0.45rem; color: inherit; cursor: pointer;
  padding: 0.22rem 0.55rem; font: inherit; font-size: 12px;
}
.gii-measure-list-add:hover { background: rgba(255,255,255,0.16); }
.gii-measure-hint { font-size: 11px; opacity: 0.72; flex: 1; min-width: 8rem; }
.gii-measure-list-items { list-style: none; margin: 0; padding: 0.25rem 0; max-height: 36vh; overflow: auto; }
.gii-measure-list-row {
  display: grid; grid-template-columns: 1fr auto auto; gap: 0.2rem; align-items: center;
  padding: 0.38rem 0.5rem;
}
.gii-measure-list-row--editing { background: rgba(100, 180, 255, 0.12); }
.gii-measure-list-label { font-variant-numeric: tabular-nums; font-size: 12px; }
.gii-measure-list-btn {
  display: grid; place-items: center;
  background: transparent; border: 1px solid rgba(255,255,255,0.18);
  border-radius: 0.45rem; color: inherit; cursor: pointer;
  width: 1.85rem; height: 1.85rem; padding: 0;
}
.gii-measure-list-btn:hover { background: rgba(255,255,255,0.08); }
.gii-measure-list-btn--danger:hover { background: rgba(220, 60, 60, 0.22); border-color: rgba(255,120,120,0.45); }
`;
  }

  private static iconRuler(): string {
    return `<span class="gii-measure-icon gii-measure-icon--ruler" aria-hidden="true"></span>`;
  }

  private static iconPencil(): string {
    return `<svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>`;
  }

  private static iconPencilSmall(): string {
    return `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>`;
  }

  private static iconCheck(): string {
    return `<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12l4 4L19 7"/></svg>`;
  }

  private static iconTrash(): string {
    return `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 7h16"/><path d="M9 7V5h6v2"/><path d="M8 7l1 12h6l1-12"/></svg>`;
  }

  private static iconEye(open: boolean): string {
    if (open) {
      return `<svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" aria-hidden="true"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="2.75"/></svg>`;
    }
    return `<svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" aria-hidden="true"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z"/><path d="M4 4l16 16"/></svg>`;
  }
}
